// Feature 13: Action Card Component

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Stack,
  LinearProgress,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  CheckCircle,
  Close,
  MoreVert,
  TrendingUp,
  AccessTime,
  Link as LinkIcon,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { ActionItem, ActionStatus, ActionPriority } from '../../types/actionQueue.types';
import ActionPriorityBadge from './ActionPriorityBadge';

interface ActionCardProps {
  action: ActionItem;
  onStatusChange?: (actionId: string, newStatus: ActionStatus) => void;
  onDismiss?: (actionId: string, reason?: string) => void;
  onViewDetails?: (actionId: string) => void;
}

const ActionCard: React.FC<ActionCardProps> = ({
  action,
  onStatusChange,
  onDismiss,
  onViewDetails,
}) => {
  const [stepsExpanded, setStepsExpanded] = useState(false);
  const [dismissDialogOpen, setDismissDialogOpen] = useState(false);
  const [dismissReason, setDismissReason] = useState('');

  const openDismissDialog = () => {
    setDismissReason(action.dismissalReason || '');
    setDismissDialogOpen(true);
  };

  const closeDismissDialog = () => {
    setDismissDialogOpen(false);
  };

  const submitDismiss = () => {
    onDismiss?.(action.id, dismissReason.trim() || undefined);
    setDismissDialogOpen(false);
  };

  const dismissalNote = action.dismissalReason || action.dismissed_reason;
  const remarksNote = action.remarks || action.notes;

  const getSuccessCriteria = () => {
    if (action.estimatedImpact?.metric && action.estimatedImpact?.projectedChange !== undefined) {
      const sign = action.estimatedImpact.projectedChange > 0 ? '+' : '';
      return `${action.estimatedImpact.metric} changes by ${sign}${action.estimatedImpact.projectedChange}% after implementation.`;
    }

    if (action.context?.metric && action.context?.delta_percent !== undefined) {
      const sign = action.context.delta_percent > 0 ? '+' : '';
      return `${action.context.metric} trend shifts by ${sign}${action.context.delta_percent.toFixed(1)}% after implementation.`;
    }

    return 'Action is completed and no longer resurfacing as an unresolved issue.';
  };

  const getEffortColor = () => {
    switch (action.effort) {
      case 'LOW':
        return 'success';
      case 'MEDIUM':
        return 'info';
      case 'HIGH':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getCategoryColor = () => {
    switch (action.category) {
      case 'CONTENT':
        return 'primary';
      case 'TECHNICAL':
        return 'secondary';
      case 'LINKS':
        return 'info';
      case 'PERFORMANCE':
        return 'success';
      case 'MONITORING':
        return 'warning';
      case 'AI_SEARCHABILITY':
        return 'error';
      default:
        return 'default';
    }
  };

  const isOverdue = action.dueDate && new Date(action.dueDate) < new Date();

  return (
    <Card
      variant="outlined"
      sx={{
        borderLeft: action.priority === 'CRITICAL' ? '4px solid' : undefined,
        borderLeftColor: action.priority === 'CRITICAL' ? 'error.main' : undefined,
        opacity: action.status === 'COMPLETED' || action.status === 'DONE' || action.status === 'DISMISSED' ? 0.7 : 1,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 2,
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 }, '&:last-child': { pb: { xs: 2, sm: 2.5, md: 3 } } }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5} gap={1}>
          <Box flex={1} minWidth={0}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
              {action.actionTypeEmoji && (
                <Typography component="span" fontSize={{ xs: '1rem', sm: '1.2rem' }}>
                  {action.actionTypeEmoji}
                </Typography>
              )}
              <Typography
                variant="h6"
                sx={{
                  textDecoration:
                    action.status === 'COMPLETED' || action.status === 'DONE' || action.status === 'DISMISSED'
                      ? 'line-through'
                      : 'none',
                  fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
                  wordBreak: 'break-word',
                  fontWeight: 600,
                }}
              >
                {action.title}
              </Typography>
              {action.isDuplicate && (
                <Chip label="Duplicate" size="small" color="warning" variant="outlined" />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', mt: 0.5, display: 'block' }}>
              Why It Matters Now
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.25,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.6,
              }}
            >
              {action.description || action.rationale}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => onViewDetails?.(action.id)} sx={{ flexShrink: 0 }}>
            <MoreVert />
          </IconButton>
        </Box>

        {/* Badges & Metadata */}
        <Stack direction="row" spacing={1} flexWrap="wrap" mb={1.5} sx={{ gap: 1 }}>
          <ActionPriorityBadge
            priority={action.priority || action.priorityLevel as ActionPriority || 'MEDIUM'}
            score={action.priorityScore}
            compact
          />
          {action.category && <Chip label={action.category} size="small" color={getCategoryColor()} />}
          {action.severity && !action.category && (
            <Chip 
              label={action.severityLabel || action.severity} 
              size="small" 
              sx={{ 
                bgcolor: action.severityColor,
                color: 'white'
              }} 
            />
          )}
          {action.effort && (
            <Chip
              label={`${action.effort} effort`}
              size="small"
              color={getEffortColor()}
              variant="outlined"
            />
          )}
          {action.pagePath && (
            <Chip
              icon={<LinkIcon fontSize="small" />}
              label={action.pagePath}
              size="small"
              variant="outlined"
              sx={{
                maxWidth: { xs: '200px', sm: '300px' },
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />
          )}
          {isOverdue && (
            <Chip
              icon={<AccessTime fontSize="small" />}
              label="Overdue"
              size="small"
              color="error"
            />
          )}
          {action.status === 'DISMISSED' && (
            <Chip label="Dismissed" size="small" color="warning" variant="outlined" />
          )}
        </Stack>

        {action.status === 'DISMISSED' && dismissalNote && (
          <Box mb={2} p={1.5} bgcolor="warning.lighter" borderRadius={1}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Dismissal Note
            </Typography>
            <Typography variant="body2">{dismissalNote}</Typography>
          </Box>
        )}

        {action.status === 'PENDING' && remarksNote && (
          <Box mb={2} p={1.5} bgcolor="info.lighter" borderRadius={1}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Re-opened Remark
            </Typography>
            <Typography variant="body2">{remarksNote}</Typography>
          </Box>
        )}

        {/* Business Value - only show if estimatedImpact exists */}
        {action.estimatedImpact && (
          <Box mb={2} p={1.5} bgcolor="info.lighter" borderRadius={1}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Business Value
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <TrendingUp color="success" fontSize="small" />
              <Typography variant="body2" fontWeight={500}>
                {action.estimatedImpact.metric}:{' '}
                {action.estimatedImpact.projectedChange > 0 ? '+' : ''}
                {action.estimatedImpact.projectedChange}%
              </Typography>
              <Chip
                label={`${Math.round(action.estimatedImpact.confidence * 100)}% confidence`}
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>
        )}
        
        {/* Business Value Context - show if context exists but no estimatedImpact */}
        {!action.estimatedImpact && action.context && (
          <Box mb={2} p={1.5} bgcolor="info.lighter" borderRadius={1}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Business Value Context
            </Typography>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              {action.context.metric && action.context.delta_percent !== undefined && (
                <>
                  <TrendingUp color={action.context.delta_percent < 0 ? 'error' : 'success'} fontSize="small" />
                  <Typography variant="body2" fontWeight={500}>
                    {action.context.metric}:{' '}
                    {action.context.delta_percent > 0 ? '+' : ''}
                    {action.context.delta_percent?.toFixed(1)}%
                  </Typography>
                </>
              )}
              {action.context.opportunity_score && (
                <Chip 
                  label={`Opportunity: ${action.context.opportunity_score.toFixed(1)}`} 
                  size="small" 
                  color="success"
                  variant="outlined"
                />
              )}
              {action.context.avg_position && (
                <Chip 
                  label={`Position: ${action.context.avg_position.toFixed(1)}`} 
                  size="small" 
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        )}

        {/* Implementation Checklist */}
        {action.steps && action.steps.length > 0 && (
          <Box mb={2} p={1.5} bgcolor="grey.50" borderRadius={1}>
            <Box 
              display="flex" 
              justifyContent="space-between" 
              alignItems="center" 
              mb={1}
              sx={{ cursor: 'pointer' }}
              onClick={() => setStepsExpanded(!stepsExpanded)}
            >
              <Typography variant="caption" color="text.secondary">
                Implementation Focus ({action.steps.filter(s => s.completed).length}/{action.steps.length})
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                {action.stepCompletionPercentage !== undefined && (
                  <Typography variant="caption" fontWeight={500}>
                    {action.stepCompletionPercentage.toFixed(0)}% complete
                  </Typography>
                )}
                <IconButton size="small" sx={{ p: 0 }}>
                  {stepsExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </IconButton>
              </Box>
            </Box>
            <Stack spacing={0.5}>
              {action.steps.slice(0, 2).map((step, idx) => (
                <Box key={idx} display="flex" alignItems="center" gap={1}>
                  <CheckCircle 
                    fontSize="small" 
                    color={step.completed ? 'success' : 'disabled'} 
                  />
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      textDecoration: step.completed ? 'line-through' : 'none',
                      color: step.completed ? 'text.disabled' : 'text.primary'
                    }}
                  >
                    {step.action}
                  </Typography>
                </Box>
              ))}
            </Stack>
            <Collapse in={stepsExpanded}>
              <Stack spacing={0.5} mt={0.5}>
                {action.steps.slice(2).map((step, idx) => (
                  <Box key={idx + 2} display="flex" alignItems="center" gap={1}>
                    <CheckCircle 
                      fontSize="small" 
                      color={step.completed ? 'success' : 'disabled'} 
                    />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        textDecoration: step.completed ? 'line-through' : 'none',
                        color: step.completed ? 'text.disabled' : 'text.primary'
                      }}
                    >
                      {step.action}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Collapse>
            {!stepsExpanded && action.steps.length > 2 && (
              <Typography 
                variant="caption" 
                color="primary" 
                sx={{ pl: 3, cursor: 'pointer', display: 'block', mt: 0.5 }}
                onClick={() => setStepsExpanded(true)}
              >
                +{action.steps.length - 2} more steps (click to expand)
              </Typography>
            )}
          </Box>
        )}

        {/* Success Criteria */}
        <Box mb={2} p={1.5} bgcolor="success.lighter" borderRadius={1}>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            Success Criteria
          </Typography>
          <Typography variant="body2">{getSuccessCriteria()}</Typography>
        </Box>

        {/* Actions */}
        <Box display="flex" gap={1}>
          {action.status === 'PENDING' && (
            <Button
              size="small"
              variant="contained"
              startIcon={<CheckCircle />}
              onClick={() => onStatusChange?.(action.id, 'IN_PROGRESS')}
            >
              Start
            </Button>
          )}
          {action.status === 'IN_PROGRESS' && (
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={() => onStatusChange?.(action.id, 'COMPLETED')}
            >
              Complete
            </Button>
          )}
          {(action.status === 'PENDING' || action.status === 'IN_PROGRESS') && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Close />}
              onClick={openDismissDialog}
            >
              Dismiss
            </Button>
          )}
        </Box>

        {/* Progress Indicator for In Progress */}
        {action.status === 'IN_PROGRESS' && (
          <Box mt={2}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              In Progress
            </Typography>
            <LinearProgress color="primary" />
          </Box>
        )}

        {/* Footer */}
        <Box mt={1.5} pt={1.5} borderTop={1} borderColor="divider">
          <Typography variant="caption" color="text.secondary">
            Created: {new Date(action.createdAt).toLocaleDateString()}
            {action.dueDate && ` • Due: ${new Date(action.dueDate).toLocaleDateString()}`}
            {action.completedAt &&
              ` • Completed: ${new Date(action.completedAt).toLocaleDateString()}`}
          </Typography>
        </Box>
      </CardContent>

      <Dialog
        open={dismissDialogOpen}
        onClose={closeDismissDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Dismiss Action</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add a note for why this action is being dismissed. This helps improve future recommendations.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            label="Dismissal reason (optional)"
            value={dismissReason}
            onChange={(e) => setDismissReason(e.target.value)}
            placeholder="Example: Not relevant to this site right now"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDismissDialog}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={submitDismiss}>
            Dismiss Action
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ActionCard;
